/**
 * Shared builders: PL `CollectionBannerWidgetData` -> domain `CustomSection`.
 *
 * Extracted verbatim from the COLLECTION_BANNER widget so the HOMEPAGE_BANNER widget can render the
 * exact same per-slide banners without duplicating the proto→domain conversion. The COLLECTION_BANNER
 * widget imports these (single source of truth); the HOMEPAGE_BANNER widget reuses `buildHeroBannerConfig`
 * + `hasBannerContent` for its slides and nests them under the existing carousel / multiple-banner
 * containers. Keep this dependency-light (no JSX) so both widgets can import it freely.
 */

import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import { THEME_TYPES } from '@src/quince-site-common/src/constants/themeTypes'
import type { ContentfulImageForMenuItem } from '@src/quince-site-common/src/domains/contentful/ContentfulImage'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import Image from '@src/quince-site-common/src/domains/Image'
import Link from '@src/quince-site-common/src/domains/Link'

import { buildWidgetTrackingMetaData, protoButtonToDomain } from './widget-utils'

import type { CollectionBannerWidgetData, Media } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const EMPTY_ARRAY: never[] = []

function toLayoutType(layoutType?: string): LAYOUT_TYPES {
  if (layoutType && layoutType in LAYOUT_TYPES) {
    return LAYOUT_TYPES[layoutType as keyof typeof LAYOUT_TYPES]
  }
  return LAYOUT_TYPES.DEFAULT
}

/**
 * PL sends `layoutType` on each CTA button; that drives stacking in HeroBanner (e.g. NON_OVERLAY = below image).
 * Always read from buttons first; fall back to section `layoutType` only when no button carries a valid value.
 */
export function resolveCollectionBannerLayoutType(data: CollectionBannerWidgetData): LAYOUT_TYPES {
  for (const btn of data.buttons) {
    const layoutType = (btn as Record<string, unknown>).layoutType
    if (typeof layoutType === 'string' && layoutType in LAYOUT_TYPES) {
      return LAYOUT_TYPES[layoutType as keyof typeof LAYOUT_TYPES]
    }
  }
  return toLayoutType(data.layoutType)
}

function toThemeType(themeType?: string): THEME_TYPES | undefined {
  if (themeType && themeType in THEME_TYPES) {
    return THEME_TYPES[themeType as keyof typeof THEME_TYPES]
  }
  return undefined
}

function normalizeMedia(media: Media | Media[] | undefined): Media[] {
  if (!media) return []
  return Array.isArray(media) ? media : [media]
}

/**
 * Extract flat image objects from proto media for HeroBanner.
 * HeroBanner (via useContentWithBackgroundImageProps) expects images: [{url, title, height, width}].
 */
function protoMediaToImages(media: Media | Media[] | undefined): Record<string, unknown>[] {
  const images: Record<string, unknown>[] = []
  for (const m of normalizeMedia(media)) {
    if (m.type !== MediaType.IMAGE || !m.content) continue
    const c = m.content as Record<string, unknown>
    if (c.url) {
      images.push(c)
    } else if (c.image && typeof c.image === 'object') {
      images.push(c.image as Record<string, unknown>)
    }
  }
  return images
}

/**
 * Convert proto media to the old media structure for CollectionPromoBanner.
 * CollectionPromoBanner expects media: [{ image: {url, title, ...} }].
 */
function protoMediaToOldMedia(media: Media | Media[] | undefined): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = []
  for (const m of normalizeMedia(media)) {
    if (m.type !== MediaType.IMAGE || !m.content) continue
    const c = m.content as Record<string, unknown>
    if (c.url) {
      result.push({ image: c })
    } else if (c.image && typeof c.image === 'object') {
      result.push({ image: c.image })
    }
  }
  return result
}

export function makeSection(overrides: Partial<CustomSection> & { id: string }): CustomSection {
  return new CustomSection({
    classNames: EMPTY_ARRAY,
    classNamesMobileOverrides: EMPTY_ARRAY,
    baseDynamicClasses: EMPTY_ARRAY,
    mobileDynamicClassesOverrides: EMPTY_ARRAY,
    influencers: EMPTY_ARRAY,
    layoutType: LAYOUT_TYPES.DEFAULT,
    navigationType: null,
    ...overrides,
  } as ConstructorParameters<typeof CustomSection>[0])
}

function protoImageToDomain(raw: Record<string, unknown>): Image {
  return Image.fromContentful(raw as unknown as ContentfulImageForMenuItem)
}

/**
 * Convert PL's containerLinks format to Link domain objects.
 * PL provides `slug` directly, while Link domain expects `navigationPath`.
 */
export function protoContainerLinksToLinks(containerLinks: Record<string, unknown>[] | undefined): Link[] {
  if (!containerLinks) return []

  return containerLinks
    .map(link => {
      const linkObj = link
      const navigationPath = linkObj.slug || linkObj.navigationPath
      if (!navigationPath) return null

      return new Link({
        id: (linkObj.id as string) || '',
        navigationPath: String(navigationPath),
        scrollToHash: (linkObj.scrollToHash as string) || '',
        experimentConfig: Array.isArray(linkObj.experimentConfig) ? linkObj.experimentConfig : undefined,
        openInNewTab:
          typeof linkObj.openInNewTab === 'boolean' || linkObj.openInNewTab === null ? linkObj.openInNewTab : undefined,
        applicablePlatforms: Array.isArray(linkObj.applicablePlatforms) ? linkObj.applicablePlatforms : undefined,
        liveShowId: typeof linkObj.liveShowId === 'string' ? linkObj.liveShowId : undefined,
        preserveQueryParams:
          typeof linkObj.preserveQueryParams === 'boolean' || linkObj.preserveQueryParams === null
            ? linkObj.preserveQueryParams
            : undefined,
        url: typeof linkObj.url === 'string' ? linkObj.url : undefined,
        text: typeof linkObj.text === 'string' ? linkObj.text : undefined,
      })
    })
    .filter((link): link is Link => link !== null)
}

/**
 * genericContents is passed through without client-side normalization.
 * The PL backend sends genericContents[i].content as a RichText-compatible object
 * (with `raw` = Document, `references`, and class-name arrays) so makeRichTextProps
 * can consume it directly. If the API ever adds `button` or `images` inside
 * genericContents, those fields would need domain conversion (e.g. Button.fromContentful).
 */
export function buildHeroBannerConfig(data: CollectionBannerWidgetData): CustomSection {
  const resolvedLayout = resolveCollectionBannerLayoutType(data)
  return makeSection({
    id: data.id,
    displayTitle: data.displayTitle,
    title: data.displayTitle,
    sectionType: CUSTOM_SECTION_TYPE.HERO_BANNER,
    buttons: data.buttons.map(b => protoButtonToDomain(b as Record<string, unknown>)) as CustomSection['buttons'],
    images: protoMediaToImages(data.media).map(protoImageToDomain) as unknown as CustomSection['images'],
    richTexts: data.richTexts as CustomSection['richTexts'],
    genericContents: data.genericContents as CustomSection['genericContents'],
    layoutType: resolvedLayout,
    themeType: toThemeType(data.themeType),
    containerLinks: protoContainerLinksToLinks(data.containerLinks) as CustomSection['containerLinks'],
    classNames: data.classNames,
    classNamesMobileOverrides: data.classNamesMobileOverrides,
    baseDynamicClasses: data.baseDynamicClasses,
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics),
  })
}

export function buildCollectionPromoBannerConfig(data: CollectionBannerWidgetData): CustomSection {
  return makeSection({
    id: data.id,
    displayTitle: data.displayTitle,
    title: data.displayTitle,
    sectionType: CUSTOM_SECTION_TYPE.COLLECTION_PROMO_BANNER,
    richTexts: data.richTexts as CustomSection['richTexts'],
    genericContents: data.genericContents as CustomSection['genericContents'],
    media: protoMediaToOldMedia(data.media) as unknown as CustomSection['media'],
    layoutType: resolveCollectionBannerLayoutType(data),
    themeType: toThemeType(data.themeType),
    classNames: data.classNames,
    classNamesMobileOverrides: data.classNamesMobileOverrides,
    baseDynamicClasses: data.baseDynamicClasses,
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics),
  })
}

/** A banner has renderable content if it carries buttons, copy, generic content, or media. */
export function hasBannerContent(data: CollectionBannerWidgetData): boolean {
  if (data.buttons.length > 0) return true
  if (data.richTexts.length > 0) return true
  if (data.genericContents.length > 0) return true
  return normalizeMedia(data.media).length > 0
}
